import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import Icon from '../components/AppIcon';

/**
 * App-wide confirmation for decisions.
 *
 * Screens each rolled their own dialog — or skipped one entirely, so several
 * approve, reject and release actions fired straight from a button click.
 * Mount the provider once and await confirm() before doing the work:
 *
 *   if (!(await confirm({ title: 'Delete user', tone: 'danger' }))) return;
 */
const ConfirmContext = createContext(null);

const TONES = {
  danger: {
    icon: 'AlertTriangle',
    iconWrap: 'bg-error-100',
    iconColor: 'text-error',
    action: 'bg-error hover:bg-error-600 text-white',
  },
  warning: {
    icon: 'AlertCircle',
    iconWrap: 'bg-warning-100',
    iconColor: 'text-warning-700',
    action: 'bg-warning-600 hover:bg-warning-700 text-white',
  },
  primary: {
    icon: 'HelpCircle',
    iconWrap: 'bg-primary-100',
    iconColor: 'text-primary',
    action: 'bg-primary hover:bg-primary-700 text-white',
  },
};

const CLOSED = { open: false, options: {} };

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(CLOSED);
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    setState(CLOSED);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  /** Resolves true when the user confirms, false when they back out. */
  const confirm = useCallback((options = {}) => {
    setState({ open: true, options });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const {
    title = 'Are you sure?',
    message = 'This action cannot be undone.',
    detail,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    tone = 'danger',
  } = state.options;

  const toneStyle = TONES[tone] || TONES.danger;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      {state.open && (
        <div
          className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 100001 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-start justify-between p-6 border-b border-secondary-200">
              <div className="flex items-center">
                <div className={`w-10 h-10 ${toneStyle.iconWrap} rounded-full flex items-center justify-center mr-3`}>
                  <Icon name={toneStyle.icon} size={20} className={toneStyle.iconColor} />
                </div>
                <h2 id="confirm-dialog-title" className="text-lg font-semibold text-text-primary">
                  {title}
                </h2>
              </div>
              <button
                onClick={() => settle(false)}
                className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-2">
              <p className="text-text-primary">{message}</p>
              {detail && <p className="text-sm text-text-secondary">{detail}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-secondary-50 rounded-b-lg">
              <button
                onClick={() => settle(false)}
                className="px-4 py-2 bg-white border border-secondary-300 text-text-primary rounded-lg hover:bg-secondary-100 transition-colors duration-150"
              >
                {cancelLabel}
              </button>
              <button
                onClick={() => settle(true)}
                autoFocus
                className={`px-4 py-2 rounded-lg transition-colors duration-150 ${toneStyle.action}`}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used inside a ConfirmProvider');
  }
  return context;
}

export default ConfirmContext;
